/**
 * The marks the comment plugin paints, and nothing else.
 *
 * Everything the panel and the composer render is Tailwind on ordinary
 * elements; only the decorations need a stylesheet, because they are class names
 * handed to ProseMirror rather than JSX. Kept in this directory for the reason
 * `editor-styles.tsx` gives for keeping its own here: a component that cannot be
 * mounted without editing a global file three directories away is a component
 * that will eventually be mounted without it.
 *
 * Two literal colours, on the same grounds `editor-styles.tsx` uses for the
 * callout accents. **Which audience a comment addresses is the one thing about
 * it that cannot be got wrong** (§8.1), so internal and client-visible marks
 * cannot both be "some ink derived from the text colour" — they have to be
 * different at a glance, in light and dark alike. Both are mid-tones used at low
 * alpha, which reads on white and on a near-black page.
 *
 * React 19 hoists a `<style>` with an `href` and a `precedence` into the head
 * and deduplicates by `href`, so this mounts once however many editors are open.
 */

const CSS = `
.mx-document-body .mx-comment-mark {
  --mx-comment: #6938ef;
  background: color-mix(in srgb, var(--mx-comment) 14%, transparent);
  box-shadow: inset 0 -2px 0 0 color-mix(in srgb, var(--mx-comment) 55%, transparent);
  border-radius: 0.15rem;
  cursor: pointer;
  transition: background-color 120ms ease-out;
}

/* §8.1 — internal is the default and the quieter of the two. A comment that
   will be readable by a client audience is the one worth spotting while
   skimming a document somebody is about to publish. */
.mx-document-body .mx-comment-mark-external { --mx-comment: #0e9384; }

.mx-document-body .mx-comment-mark:hover {
  background: color-mix(in srgb, var(--mx-comment) 22%, transparent);
}

.mx-document-body .mx-comment-mark-selected {
  background: color-mix(in srgb, var(--mx-comment) 30%, transparent);
  box-shadow: inset 0 -2px 0 0 var(--mx-comment);
}

/* §8.2 — a suggestion is a proposal to replace these words, so the mark says
   "this text is under offer" rather than "somebody said something here". */
.mx-document-body .mx-comment-mark-suggestion {
  box-shadow: inset 0 -2px 0 0 transparent;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-decoration-color: var(--mx-comment);
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

/* A block-level anchor — a comment on an image, or on a paragraph as a whole.
   It is a node decoration rather than a range, so it gets a rule of its own:
   the inset underline would sit under the block's last line and read as a
   comment on that line. */
.mx-document-body p.mx-comment-mark,
.mx-document-body h1.mx-comment-mark,
.mx-document-body h2.mx-comment-mark,
.mx-document-body h3.mx-comment-mark,
.mx-document-body h4.mx-comment-mark,
.mx-document-body h5.mx-comment-mark,
.mx-document-body h6.mx-comment-mark,
.mx-document-body pre.mx-comment-mark,
.mx-document-body img.mx-comment-mark,
.mx-document-body table.mx-comment-mark,
.mx-document-body aside.mx-comment-mark,
.mx-document-body div.mx-comment-mark {
  background: color-mix(in srgb, var(--mx-comment) 8%, transparent);
  box-shadow: none;
  border-left: 3px solid color-mix(in srgb, var(--mx-comment) 60%, transparent);
  padding-left: 0.6em;
  margin-left: -0.75em;
}
`;

export function CommentStyles() {
  return (
    <style href="matchbox-document-comments" precedence="medium">
      {CSS}
    </style>
  );
}
